import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Tooltip,
  Menu, MenuItem, ListItemIcon, ListItemText
} from '@mui/material';
import { Image as ImageIcon, RotateCcw, Trash2, X, FileText, AlertCircle, ChevronLeft, ChevronRight, Sparkles, Check, ShieldCheck, Flame, ChevronDown, Wand2, Sun, Layers, Upload, Power, Lightbulb, Download } from 'lucide-react';
import { API_BASE_URL } from '../utils/api';
import { useTeam } from '../context/TeamContext';

const ACTIVE_STATUSES = new Set(['queued', 'planning', 'running', 'uploading']);

// Default text pre-filled into the editing form per preset; user is free to
// edit or clear before submitting.
const EDITING_DEFAULT_PROMPTS = {
  imageEdit:    'Enhance image quality, sharpen fine textures, restore natural colors and contrast.',
  imageFusion:  '',
  imageRelight: 'Relight with soft window glow and golden-hour warmth.'
};

const EDITING_PRESETS_FULL = [
  { key: 'imageEdit',    label: 'Image Edit',    Icon: Wand2 },
  { key: 'imageFusion',  label: 'Image Fusion',  Icon: Layers },
  { key: 'imageRelight', label: 'Image Relight', Icon: Sun }
];

// Subset available from the Image Viewer's "Edit Image" menu — fusion needs a
// dedicated context (usually combining subject + background) and doesn't make
// sense as a one-click action on a gallery image.
const VIEWER_EDIT_PRESETS = EDITING_PRESETS_FULL.filter((p) => p.key !== 'imageFusion');

function statusProgress(status) {
  if (status === 'queued') return 10;
  if (status === 'planning') return 25;
  if (status === 'running') return 55;
  if (status === 'uploading') return 88;
  return 100;
}

function CircularProgressBar({ value, label }) {
  const size = 72;
  const stroke = 5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (value / 100) * circumference;
  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={stroke}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="#a78bfa" strokeWidth={stroke}
          strokeDasharray={`${dash} ${circumference}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dasharray 0.4s ease' }}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#a78bfa', fontWeight: 700, fontSize: '1.1rem',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
        }}
      >
        {label}
      </Box>
    </Box>
  );
}

const PlaceholderTile = React.memo(function PlaceholderTile({ job }) {
  const total = Math.max(1, job.progressTotal || 1);
  const done = Math.max(0, Math.min(total, job.progressDone || 0));
  const remaining = Math.max(0, total - done);
  // While uploading the last batch of files, show the status-based progress;
  // otherwise reflect real per-call progress.
  const pct = job.status === 'uploading'
    ? Math.max(statusProgress(job.status), (done / total) * 100)
    : (done / total) * 100;
  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: '1 / 1',
        borderRadius: '10px',
        border: '1px solid rgba(139,92,246,0.3)',
        bgcolor: 'rgba(139,92,246,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 0.75,
        overflow: 'hidden',
      }}
    >
      <CircularProgressBar value={pct} label={remaining} />
      <Typography sx={{ fontSize: '0.65rem', color: '#a78bfa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {job.status}
      </Typography>
    </Box>
  );
});

/**
 * Trash button with two-step confirm: first click arms (red outline + tooltip
 * hint), a second click within 4s triggers `onConfirm`. Clicking anywhere
 * else, or waiting out the timer, disarms it.
 */
function ArmedDeleteButton({ onConfirm, sx, iconSize = 13, className }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <Tooltip title={armed ? 'Click again to delete' : 'Delete'}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          if (armed) { setArmed(false); onConfirm(); }
          else { setArmed(true); }
        }}
        className={className}
        sx={{
          bgcolor: armed ? 'rgba(248,113,113,0.25)' : 'rgba(0,0,0,0.55)',
          color: armed ? '#f87171' : 'rgba(255,255,255,0.8)',
          border: armed ? '1px solid rgba(248,113,113,0.6)' : '1px solid transparent',
          transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
          '&:hover': { bgcolor: armed ? 'rgba(248,113,113,0.35)' : 'rgba(0,0,0,0.75)', color: '#f87171' },
          ...sx,
          // When armed, keep the button visible even if parent hover hides it.
          ...(armed ? { opacity: '1 !important' } : {})
        }}
      >
        <Trash2 size={iconSize} />
      </IconButton>
    </Tooltip>
  );
}

function formatCountdown(ms) {
  if (ms == null || ms <= 0) return '0:00';
  const totalSec = Math.ceil(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Pod status pill rendered in the centre of the Generation header. Polls
 * /pod-status every 5s for RunPod state + health + auto-shutdown deadline,
 * and runs a 1s-tick local countdown against the deadline so the visible
 * number moves smoothly between polls. Clicking the pill while the pod is
 * running opens a modal with Shut down / Extend actions.
 *
 * Hidden entirely when no pod is configured for the team.
 */
function PodPill({ getToken, teamId }) {
  const [state, setState] = useState(null); // { enabled, desiredStatus, healthy, shutdownAt }
  const [acting, setActing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const poll = useCallback(async () => {
    if (!teamId) return;
    try {
      const token = await getToken();
      const res = await fetch(
        `${API_BASE_URL}/api/image-generation/pod-status?teamId=${encodeURIComponent(teamId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        // Silently hide on error — don't clutter the header with red pills.
        setState((prev) => prev || { enabled: false });
        return;
      }
      const data = await res.json();
      setState(data);
    } catch (_) {
      setState((prev) => prev || { enabled: false });
    }
  }, [teamId, getToken]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [poll]);

  useEffect(() => {
    // Smooth 1s ticker drives the displayed countdown between polls.
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const doStart = useCallback(async () => {
    setActing(true);
    try {
      const token = await getToken();
      await fetch(`${API_BASE_URL}/api/image-generation/pod-start?teamId=${encodeURIComponent(teamId)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      await poll();
    } finally { setActing(false); }
  }, [teamId, getToken, poll]);

  const doStop = useCallback(async () => {
    setActing(true);
    try {
      const token = await getToken();
      await fetch(`${API_BASE_URL}/api/image-generation/pod-stop?teamId=${encodeURIComponent(teamId)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setModalOpen(false);
      await poll();
    } finally { setActing(false); }
  }, [teamId, getToken, poll]);

  const doExtend = useCallback(async () => {
    setActing(true);
    try {
      const token = await getToken();
      await fetch(`${API_BASE_URL}/api/image-generation/pod-extend?teamId=${encodeURIComponent(teamId)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setModalOpen(false);
      await poll();
    } finally { setActing(false); }
  }, [teamId, getToken, poll]);

  if (!state || !state.enabled) return null;

  // Serverless endpoints have no controllable lifecycle (RunPod autoscales),
  // so we only render a passive health indicator — no Start/Stop/Extend.
  const isServerless = state.kind === 'serverless';

  const desired = state.desiredStatus || 'UNKNOWN';
  const isRunning = desired === 'RUNNING';
  const isStopped = desired === 'EXITED' || desired === 'TERMINATED';
  const shutdownAt = state.shutdownAt ? new Date(state.shutdownAt).getTime() : null;
  const remaining = shutdownAt ? shutdownAt - now : null;
  const isReady = isServerless ? !!state.healthy : (isRunning && state.healthy);

  let dotColor = '#6b7280';
  let label = desired;
  let bgColor = 'rgba(107,114,128,0.12)';
  let borderColor = 'rgba(107,114,128,0.35)';
  let textColor = '#9ca3af';
  let handler = null;
  let tooltipText = '';

  if (isServerless) {
    if (state.healthy) {
      dotColor = '#4ade80';
      bgColor = 'rgba(74,222,128,0.12)';
      borderColor = 'rgba(74,222,128,0.35)';
      textColor = '#4ade80';
      label = 'Serverless · ready';
      tooltipText = 'Serverless endpoint is reachable';
    } else {
      dotColor = '#9ca3af';
      label = 'Serverless';
      tooltipText = 'Serverless endpoint configured — health probe not responding';
    }
  } else if (isStopped) {
    dotColor = '#9ca3af';
    label = 'Start Pod';
    handler = doStart;
    tooltipText = 'Pod is stopped — click to start';
  } else if (isRunning && isReady) {
    dotColor = '#4ade80';
    bgColor = 'rgba(74,222,128,0.12)';
    borderColor = 'rgba(74,222,128,0.35)';
    textColor = '#4ade80';
    label = remaining != null && remaining > 0
      ? `Pod · ${formatCountdown(remaining)}`
      : 'Pod · running';
    handler = () => setModalOpen(true);
    tooltipText = 'Pod is running — click for options';
  } else if (isRunning && !isReady) {
    dotColor = '#fbbf24';
    bgColor = 'rgba(251,191,36,0.12)';
    borderColor = 'rgba(251,191,36,0.35)';
    textColor = '#fbbf24';
    label = 'Warming up…';
    tooltipText = 'VM is up but /health not responding yet';
  }

  return (
    <>
      <Tooltip title={tooltipText}>
        <Box
          component="button"
          type="button"
          onClick={handler || undefined}
          disabled={acting || !handler}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            fontSize: '0.72rem',
            fontWeight: 600,
            px: 1, py: 0.35,
            borderRadius: '999px',
            bgcolor: bgColor,
            color: textColor,
            border: `1px solid ${borderColor}`,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            cursor: handler ? 'pointer' : 'default',
            opacity: acting ? 0.6 : 1,
            transition: 'background-color 0.15s ease',
            '&:hover': handler ? { bgcolor: bgColor.replace('0.12', '0.2') } : {}
          }}
        >
          {acting ? (
            <CircularProgress size={10} sx={{ color: textColor }} />
          ) : (
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: dotColor,
              boxShadow: isReady ? `0 0 6px ${dotColor}` : 'none'
            }} />
          )}
          {label}
        </Box>
      </Tooltip>

      <Dialog
        open={modalOpen}
        onClose={() => !acting && setModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none', border: '1px solid', borderColor: 'divider' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1rem' }}>
          <Power size={16} style={{ color: '#4ade80' }} />
          Pod is running
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Auto-shutdown in {remaining != null && remaining > 0 ? formatCountdown(remaining) : '—'}. Extend to reset the timer to 30 minutes, or shut down now to stop billing immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setModalOpen(false)} disabled={acting}>Cancel</Button>
          <Button onClick={doStop} disabled={acting} color="error" variant="outlined">
            Shut down
          </Button>
          <Button onClick={doExtend} disabled={acting} variant="contained" sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}>
            Extend 30 min
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const FailedTile = React.memo(function FailedTile({ job, onDeleteJob }) {
  return (
    <Tooltip title={job.error?.message || 'Generation failed'}>
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '1 / 1',
          borderRadius: '10px',
          border: '1px solid rgba(248,113,113,0.4)',
          bgcolor: 'rgba(248,113,113,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 0.5,
          cursor: 'default',
        }}
      >
        <AlertCircle size={28} color="#f87171" />
        <Typography sx={{ fontSize: '0.62rem', color: '#f87171', fontWeight: 600, textTransform: 'uppercase' }}>
          failed
        </Typography>
        <ArmedDeleteButton
          onConfirm={() => onDeleteJob(job.jobId)}
          iconSize={12}
          sx={{ position: 'absolute', top: 4, right: 4 }}
        />
      </Box>
    </Tooltip>
  );
});

const ImageTile = React.memo(function ImageTile({ image, job, onDeleteImage, onOpen }) {
  return (
    <Box
      onClick={() => onOpen(image.r2Key)}
      sx={{
        position: 'relative',
        aspectRatio: '1 / 1',
        borderRadius: '10px',
        border: '1px solid rgba(255,255,255,0.08)',
        bgcolor: 'rgba(0,0,0,0.35)',
        overflow: 'hidden',
        cursor: 'zoom-in',
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: 'rgba(139,92,246,0.5)',
          '& .overlay': { opacity: 1 }
        }
      }}
    >
      <Box
        component="img"
        src={image.previewUrl || image.url}
        alt=""
        loading="lazy"
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
      <ArmedDeleteButton
        className="overlay"
        onConfirm={() => onDeleteImage(job.jobId, image)}
        sx={{
          position: 'absolute', top: 4, right: 4,
          opacity: 0
        }}
      />
    </Box>
  );
});

function ContextDialog({ open, initial, onClose, onSave }) {
  const [text, setText] = useState(initial || '');
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  useEffect(() => { setText(initial || ''); }, [initial, open]);

  const dirty = text !== (initial || '');

  const attemptClose = useCallback(() => {
    if (dirty) { setConfirmDiscard(true); return; }
    onClose();
  }, [dirty, onClose]);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      await onSave(text);
    } finally {
      setSaving(false);
    }
  }, [text, onSave]);

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          // Reason can be 'backdropClick' or 'escapeKeyDown' — block if dirty.
          if (dirty) { setConfirmDiscard(true); return; }
          onClose();
        }}
        disableEscapeKeyDown={dirty}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <FileText size={16} style={{ color: '#a78bfa' }} />
          <Typography component="span" sx={{ fontWeight: 600 }}>Context</Typography>
          <IconButton
            aria-label="close"
            onClick={attemptClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            multiline
            fullWidth
            minRows={6}
            placeholder="Notes, character sheets, scene details…"
            autoFocus
            sx={{
              '& textarea': {
                fontSize: '0.88rem',
                lineHeight: 1.55,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                overflow: 'hidden !important'
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You have edits that haven't been saved. Close and lose them?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDiscard(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={() => { setConfirmDiscard(false); onClose(); }}>
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function formatTimings(t) {
  if (!t || typeof t !== 'object') return '';
  // fal.ai typically returns { inference: number } in seconds.
  const out = [];
  for (const [k, v] of Object.entries(t)) {
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    const s = v >= 10 ? v.toFixed(1) : v.toFixed(2);
    out.push(`${k} ${s}s`);
  }
  return out.join(' · ');
}

function LightboxMeta({ item }) {
  if (!item) return null;
  const rows = [];
  if (item.width && item.height) rows.push(['Size', `${item.width}×${item.height}`]);
  const timingsStr = formatTimings(item.timings);
  if (timingsStr) rows.push(['Timings', timingsStr]);
  if (item.seed != null) rows.push(['Seed', String(item.seed)]);
  if (!rows.length) return null;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.25, rowGap: 0.25 }}>
      {rows.map(([k, v]) => (
        <React.Fragment key={k}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.disabled' }}>
            {k}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.72rem',
              color: 'text.primary',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              wordBreak: 'break-all'
            }}
          >
            {v}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );
}

function LightboxActionRow({ current, onUpscale, onEdit, runUpscale, upscalingKey, getToken, teamId }) {
  const [anchor, setAnchor] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const upscaling = !!upscalingKey;
  const disabled = upscaling;
  const canUpscale = !!onUpscale && !current.upscaled;
  const canEdit = !!onEdit;

  // R2 public URLs don't return CORS headers for our origin, so we fetch
  // through the server's /download proxy (same-origin, Bearer auth) which
  // streams the object with Content-Disposition: attachment. The blob+anchor
  // dance is what actually triggers Save-As — `<a download>` to a cross-
  // origin URL just opens it in a new tab.
  const runDownload = async () => {
    if (!current.r2Key || !teamId || !getToken || downloading) return;
    setDownloading(true);
    try {
      const token = await getToken();
      const url = `${API_BASE_URL}/api/image-generation/download`
        + `?teamId=${encodeURIComponent(teamId)}`
        + `&r2Key=${encodeURIComponent(current.r2Key)}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = current.r2Key.split('/').pop() || 'image.webp';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error('[download] failed:', e && e.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {canUpscale && (
        <Button
          size="small"
          variant="contained"
          disabled={disabled}
          onClick={runUpscale}
          startIcon={upscalingKey === current.r2Key
            ? <CircularProgress size={12} color="inherit" />
            : <Sparkles size={14} />}
          sx={{
            textTransform: 'none',
            bgcolor: '#8b5cf6',
            '&:hover': { bgcolor: '#7c3aed' },
            fontWeight: 600,
            py: 0.4, px: 1.25
          }}
        >
          {upscalingKey === current.r2Key ? 'Upscaling…' : 'Upscale'}
        </Button>
      )}
      {canEdit && (
        <Button
          size="small"
          variant="contained"
          disabled={disabled}
          onClick={(e) => setAnchor(e.currentTarget)}
          endIcon={<ChevronDown size={14} />}
          startIcon={<Wand2 size={14} />}
          sx={{
            textTransform: 'none',
            bgcolor: 'rgba(99,102,241,0.75)',
            '&:hover': { bgcolor: 'rgba(99,102,241,0.9)' },
            fontWeight: 600,
            py: 0.4, px: 1.25
          }}
        >
          Edit Image
        </Button>
      )}
      <Tooltip title="Download image">
        <span>
          <Button
            size="small"
            variant="contained"
            disabled={disabled || downloading || !current.r2Key}
            onClick={runDownload}
            sx={{
              textTransform: 'none',
              bgcolor: 'rgba(99,102,241,0.75)',
              '&:hover': { bgcolor: 'rgba(99,102,241,0.9)' },
              fontWeight: 600,
              minWidth: 0,
              py: 0.4, px: 1.1,
              // Match neighbor button height — text content gives them a
              // line-height envelope of ~22.75px (0.8125rem * 1.75); an icon-
              // only Button collapses to icon height (14px) without this.
              lineHeight: 1.75,
              fontSize: '0.8125rem'
            }}
          >
            {downloading
              ? <CircularProgress size={14} color="inherit" />
              : <Download size={14} />}
          </Button>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {VIEWER_EDIT_PRESETS.map(({ key, label, Icon: PIcon }) => (
          <MenuItem
            key={key}
            onClick={() => { setAnchor(null); onEdit && onEdit(key, current.url); }}
          >
            <ListItemIcon sx={{ minWidth: 28, color: '#a5b4fc' }}>
              <PIcon size={16} />
            </ListItemIcon>
            <ListItemText
              primary={label}
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

function LightboxDialog({ items, index, onClose, onIndexChange, onUpscale, onEdit, getToken, teamId }) {
  const current = index != null && items && items[index] ? items[index] : null;

  // Desktop check — show the prompt sidebar only on wide screens.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 900
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Track when the current image has fully loaded — the nav arrows wait
  // for it so they don't hover next to empty space. Resets on every slide.
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(false); }, [current?.url]);

  // The sidebar is a different story: once it's shown the first time we
  // keep it mounted forever, because unmounting/remounting it on each slide
  // change reflows the image and makes the viewer jump around. Its inner
  // content is blanked while the next image loads.
  const [sidebarShown, setSidebarShown] = useState(false);
  useEffect(() => {
    if (isDesktop && loaded) setSidebarShown(true);
  }, [isDesktop, loaded]);

  // Upscale state keyed by r2Key — multiple slides don't share a spinner.
  const [upscalingKey, setUpscalingKey] = useState(null);
  const [upscaleError, setUpscaleError] = useState('');
  useEffect(() => { setUpscaleError(''); }, [current?.r2Key]);

  const runUpscale = useCallback(async () => {
    if (!current || !onUpscale || upscalingKey) return;
    setUpscalingKey(current.r2Key);
    setUpscaleError('');
    try {
      await onUpscale(current.jobId, current.r2Key);
    } catch (e) {
      setUpscaleError(e.message || 'Upscale failed');
    } finally {
      setUpscalingKey(null);
    }
  }, [current, onUpscale, upscalingKey]);

  const goPrev = useCallback(() => {
    if (!items || items.length === 0 || index == null) return;
    onIndexChange((index - 1 + items.length) % items.length);
  }, [items, index, onIndexChange]);

  const goNext = useCallback(() => {
    if (!items || items.length === 0 || index == null) return;
    onIndexChange((index + 1) % items.length);
  }, [items, index, onIndexChange]);

  useEffect(() => {
    if (!current) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, goPrev, goNext]);

  if (!current) return null;

  const promptText = current.prompt || '';
  const tags = Array.isArray(current.tags) ? current.tags : [];
  const rating = current.rating === 'explicit' ? 'explicit' : 'safe';
  const isEditingItem = current.kind === 'editing';
  const PRESET_LABELS = {
    zImageTurbo: 'Z-Image-Turbo',
    qwenImage: 'Qwen-Image',
    imageEdit: 'Image Edit',
    imageFusion: 'Image Fusion',
    imageRelight: 'Image Relight'
  };
  const presetLabel = PRESET_LABELS[current.presetKey] || 'Z-Image-Turbo';

  const navBtnSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 3,
    bgcolor: 'rgba(0,0,0,0.55)',
    color: 'white',
    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: 'transparent',
          boxShadow: 'none',
          backgroundImage: 'none',
          m: 2,
          maxHeight: '92vh',
          maxWidth: '96vw',
          overflow: 'visible'
        }
      }}
    >
      <Box sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: isDesktop ? 'row' : 'column',
        alignItems: 'stretch',
        gap: isDesktop ? 2 : 1,
        maxHeight: '92vh'
      }}>
        {/* Image panel (relative so nav arrows + close anchor to it) */}
        <Box sx={{ position: 'relative', flex: '0 1 auto', display: 'flex' }}>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute', top: 8, right: 8, zIndex: 4,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
            }}
          >
            <X size={18} />
          </IconButton>
          {loaded && items.length > 1 && (
            <>
              <IconButton onClick={goPrev} sx={{ ...navBtnSx, left: 8 }} size="small">
                <ChevronLeft size={22} />
              </IconButton>
              <IconButton onClick={goNext} sx={{ ...navBtnSx, right: 8 }} size="small">
                <ChevronRight size={22} />
              </IconButton>
            </>
          )}
          <Box
            component="img"
            src={current.url}
            alt=""
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
            sx={{
              display: 'block',
              maxWidth: isDesktop ? '62vw' : '96vw',
              maxHeight: '92vh',
              objectFit: 'contain',
              borderRadius: '8px'
            }}
          />
        </Box>

        {/* Prompt sidebar — desktop only. Mounts on first image load and
            stays mounted; inner content blanks out while the next image
            loads so the outer layout never reflows. */}
        {isDesktop && sidebarShown && (
          <Box
            sx={{
              width: 340,
              flexShrink: 0,
              bgcolor: 'rgba(18,18,20,0.92)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '8px',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
              maxHeight: '92vh',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
                Prompt
              </Typography>
              <Typography sx={{ ml: 'auto', fontSize: '0.7rem', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
                {index + 1} / {items.length}
              </Typography>
            </Box>
            <Box
              className="vr-scroll-y"
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                p: 1.25,
                borderRadius: '6px',
                bgcolor: 'rgba(0,0,0,0.35)',
                border: '1px solid rgba(255,255,255,0.05)',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.75rem',
                lineHeight: 1.55,
                color: 'text.primary',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {loaded
                ? (promptText || <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>)
                : null}
            </Box>
            {loaded && (onUpscale || onEdit) && (
              <LightboxActionRow
                current={current}
                onUpscale={onUpscale}
                onEdit={onEdit}
                runUpscale={runUpscale}
                upscalingKey={upscalingKey}
                getToken={getToken}
                teamId={teamId}
              />
            )}
            {loaded && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                {!isEditingItem && (
                  <Tooltip title={rating === 'explicit'
                    ? 'Grok rated this scene as explicit (visible sex actions or genitals)'
                    : 'Grok rated this scene as safe'}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontSize: '0.66rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        px: 0.75, py: 0.2,
                        borderRadius: '4px',
                        bgcolor: rating === 'explicit' ? 'rgba(248,113,113,0.14)' : 'rgba(148,163,184,0.14)',
                        color: rating === 'explicit' ? '#f87171' : '#94a3b8',
                        border: rating === 'explicit'
                          ? '1px solid rgba(248,113,113,0.4)'
                          : '1px solid rgba(148,163,184,0.35)',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'
                      }}
                    >
                      {rating}
                    </Box>
                  </Tooltip>
                )}
                <Tooltip title={`Pipeline preset: ${presetLabel}`}>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontSize: '0.66rem',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      px: 0.75, py: 0.2,
                      borderRadius: '4px',
                      bgcolor: 'rgba(99,102,241,0.12)',
                      color: '#a5b4fc',
                      border: '1px solid rgba(99,102,241,0.35)',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'
                    }}
                  >
                    {presetLabel}
                  </Box>
                </Tooltip>
                {current.upscaled && (
                  <Tooltip title="Image has been upscaled">
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.35,
                        fontSize: '0.66rem',
                        fontWeight: 600,
                        px: 0.75, py: 0.2,
                        borderRadius: '4px',
                        bgcolor: 'rgba(74,222,128,0.12)',
                        color: '#4ade80',
                        border: '1px solid rgba(74,222,128,0.35)',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'
                      }}
                    >
                      <Check size={11} strokeWidth={3} /> Upscaled
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}
            {loaded && upscaleError && (
              <Typography sx={{ fontSize: '0.68rem', color: '#f87171', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                Upscale failed: {upscaleError}
              </Typography>
            )}
            {loaded && tags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {tags.map((t, i) => (
                  <Box
                    key={`${t}-${i}`}
                    component="span"
                    sx={{
                      fontSize: '0.66rem',
                      px: 0.75, py: 0.25,
                      borderRadius: '999px',
                      bgcolor: 'rgba(139,92,246,0.12)',
                      color: '#a78bfa',
                      border: '1px solid rgba(139,92,246,0.25)'
                    }}
                  >
                    {t}
                  </Box>
                ))}
              </Box>
            )}
            {loaded && <LightboxMeta item={current} />}
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

// How long each Grok idea sits on screen before sliding away. Picked so the
// average reader can finish a 6–10 word Russian title without feeling rushed.
const IDEA_HOLD_MS = 2500;

/**
 * Plays in place of the prompt input while a Grok-mode job is planning/running.
 * Two phases:
 *   1. waiting — variations not back yet (status queued/planning, no titles).
 *      Renders a shimmery "Грок придумывает идеи…" placeholder.
 *   2. parade — slides each variation.title in from below, holds for
 *      IDEA_HOLD_MS, then slides it up and the next one in. Items stay mounted
 *      and translate via CSS so transitions are smooth and we never reflow.
 *
 * Calls onDone() when the last idea has finished its hold so the parent can
 * restore the prompt input.
 */
function IdeaParade({ ideas, currentIdx, onAdvance }) {
  const phase = ideas.length === 0 ? 'waiting' : 'parade';

  useEffect(() => {
    if (phase !== 'parade') return undefined;
    if (currentIdx >= ideas.length) return undefined;
    const t = setTimeout(onAdvance, IDEA_HOLD_MS);
    return () => clearTimeout(t);
  }, [phase, currentIdx, ideas.length, onAdvance]);

  return (
    <Box sx={{
      position: 'relative',
      borderRadius: '10px',
      border: '1px solid rgba(250,204,21,0.25)',
      bgcolor: 'rgba(250,204,21,0.05)',
      px: 1.75, py: 1.5,
      minHeight: 64,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      overflow: 'hidden',
      // Subtle shimmer that sweeps across the whole panel.
      '@keyframes paradeShimmer': {
        '0%':   { transform: 'translateX(-110%)' },
        '100%': { transform: 'translateX(110%)' }
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0, left: 0, bottom: 0,
        width: '40%',
        background: 'linear-gradient(90deg, transparent 0%, rgba(250,204,21,0.08) 50%, transparent 100%)',
        animation: 'paradeShimmer 2.6s ease-in-out infinite',
        pointerEvents: 'none'
      }
    }}>
      <Box sx={{
        flexShrink: 0,
        width: 36, height: 36,
        borderRadius: '50%',
        bgcolor: 'rgba(250,204,21,0.15)',
        border: '1px solid rgba(250,204,21,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#facc15',
        '@keyframes paradePulse': {
          '0%, 100%': { boxShadow: '0 0 0 0 rgba(250,204,21,0.35)' },
          '50%':      { boxShadow: '0 0 0 6px rgba(250,204,21,0)' }
        },
        animation: 'paradePulse 1.8s ease-in-out infinite'
      }}>
        <Lightbulb size={18} strokeWidth={2.2} />
      </Box>

      <Box sx={{
        flex: 1, minWidth: 0,
        position: 'relative',
        height: 36,
        overflow: 'hidden'
      }}>
        {phase === 'waiting' && (
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center',
            color: 'rgba(250,204,21,0.85)',
            fontSize: '0.92rem',
            fontWeight: 500,
            letterSpacing: '0.01em',
            '@keyframes paradeWaitingShine': {
              '0%':   { backgroundPosition: '-200% 0' },
              '100%': { backgroundPosition: '200% 0' }
            },
            background: 'linear-gradient(90deg, rgba(250,204,21,0.55) 0%, #facc15 50%, rgba(250,204,21,0.55) 100%)',
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'paradeWaitingShine 2.2s linear infinite'
          }}>
            Грок придумывает идеи…
          </Box>
        )}
        {phase === 'parade' && ideas.map((idea, i) => {
          // Layered approach — every idea stays mounted at absolute inset 0,
          // and we translate them so exactly one is centred on screen at any
          // moment. Older ideas sit above the viewport, upcoming ones below.
          const offset = i - currentIdx;
          const translate = offset === 0 ? 0 : (offset < 0 ? -120 : 120);
          const visible = offset === 0;
          return (
            <Box
              key={i}
              sx={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center',
                fontSize: '0.92rem',
                fontWeight: 500,
                letterSpacing: '0.01em',
                color: '#fde68a',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                transform: `translateY(${translate}%)`,
                opacity: visible ? 1 : 0,
                transition: 'transform 520ms cubic-bezier(0.32, 0.72, 0.24, 1), opacity 380ms ease',
                willChange: 'transform, opacity'
              }}
            >
              {idea.title}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

const PAGE_SIZE = 60;

/**
 * Merge freshly-fetched jobs into the existing list — upsert by jobId so a
 * poll can refresh in-flight jobs without wiping already-loaded older pages.
 * Anything the user deleted this session is filtered out defensively: a poll
 * that runs before the server has processed the DELETE would otherwise revive
 * ghosts.
 */
function mergeJobs(existing, fresh, deadJobs, deadR2Keys) {
  const map = new Map(existing.map((j) => [j.jobId, j]));
  for (const j of fresh) {
    if (deadJobs.has(j.jobId)) continue;
    const cleanedImages = (j.images || []).filter((im) => !deadR2Keys.has(im.r2Key));
    map.set(j.jobId, { ...j, images: cleanedImages });
  }
  return Array.from(map.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

export default function GenerationPanel({ getToken }) {
  const { activeTeamId } = useTeam();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [prompt, setPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [contextOpen, setContextOpen] = useState(false);
  const [contextInitial, setContextInitial] = useState('');
  // Track the open image by its stable r2Key rather than numeric index — new
  // generations prepend to the gallery and would otherwise shift the viewer.
  const [lightboxKey, setLightboxKey] = useState(null);

  // ── Idea parade ───────────────────────────────────────────────────────
  // After a Grok-mode Generate, we replace the prompt input with a panel
  // that cycles through the titles Grok came up with. paradeJobId pins the
  // parade to a specific job so polling refresh doesn't accidentally trigger
  // it for older runs; paradeIdeaIdx walks through that job's variations.
  const [paradeJobId, setParadeJobId] = useState(null);
  const [paradeIdeaIdx, setParadeIdeaIdx] = useState(0);
  // Stable across renders so polling jobs (every 2s while a job is active)
  // doesn't keep resetting the IdeaParade hold-timer.
  const advanceParade = useCallback(() => setParadeIdeaIdx((i) => i + 1), []);

  // ── Editing tab state ──────────────────────────────────────────────
  const [mainTab, setMainTab] = useState('generation');
  const [editingPreset, setEditingPreset] = useState('imageEdit');
  const [editingImageUrl, setEditingImageUrl] = useState('');
  const [editingPrompt, setEditingPrompt] = useState(EDITING_DEFAULT_PROMPTS.imageEdit);
  const [editingError, setEditingError] = useState('');
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [uploadingSource, setUploadingSource] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Client-side tombstones so optimistic deletes survive in-flight polls and
  // server-side async processing. Refs (not state) so they don't invalidate
  // the load() callback and retrigger a fetch loop.
  const deletedJobIdsRef = useRef(new Set());
  const deletedR2KeysRef = useRef(new Set());

  // Reset everything when switching teams.
  useEffect(() => {
    setJobs([]);
    setTotal(0);
    setParadeJobId(null);
    setParadeIdeaIdx(0);
    deletedJobIdsRef.current = new Set();
    deletedR2KeysRef.current = new Set();
  }, [activeTeamId]);

  // Fetches the FIRST page (offset 0). Used for the initial load and for the
  // in-flight polling refresh. Merges into the existing list so already-loaded
  // older pages are preserved.
  const load = useCallback(async (silent = false) => {
    if (!activeTeamId) return;
    if (!silent) setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(
        `${API_BASE_URL}/api/image-generation?teamId=${encodeURIComponent(activeTeamId)}&limit=${PAGE_SIZE}&offset=0`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (typeof data.total === 'number') setTotal(data.total);
      setJobs((prev) => mergeJobs(prev, data.items || [], deletedJobIdsRef.current, deletedR2KeysRef.current));
    } catch (e) {
      setError(e.message || 'Failed to load jobs');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [activeTeamId, getToken]);

  useEffect(() => { load(); }, [load]);

  const loadMore = useCallback(async () => {
    if (!activeTeamId) return;
    setLoadingMore(true);
    try {
      const token = await getToken();
      const offset = jobs.length;
      const res = await fetch(
        `${API_BASE_URL}/api/image-generation?teamId=${encodeURIComponent(activeTeamId)}&limit=${PAGE_SIZE}&offset=${offset}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (typeof data.total === 'number') setTotal(data.total);
      setJobs((prev) => mergeJobs(prev, data.items || [], deletedJobIdsRef.current, deletedR2KeysRef.current));
    } catch (e) {
      setError(e.message || 'Failed to load jobs');
    } finally {
      setLoadingMore(false);
    }
  }, [activeTeamId, getToken, jobs.length]);

  const hasMore = jobs.length < total;

  const hasActive = useMemo(
    () => jobs.some((j) => ACTIVE_STATUSES.has(j.status)),
    [jobs]
  );
  useEffect(() => {
    if (!hasActive) return undefined;
    const id = setInterval(() => load(true), 2000);
    return () => clearInterval(id);
  }, [hasActive, load]);

  const submitGenerate = useCallback(async (directRating) => {
    const text = prompt.trim();
    if (!text || !activeTeamId) return;
    setSubmitting(true);
    setError('');
    try {
      const token = await getToken();
      const body = { teamId: activeTeamId, prompt: text };
      if (directRating) body.directRating = directRating;
      const res = await fetch(`${API_BASE_URL}/api/image-generation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.message || `HTTP ${res.status}`);
      }
      const created = await res.json().catch(() => null);
      setPrompt('');
      // Direct-rating jobs skip Grok entirely — no titles to parade.
      if (!directRating && created?.jobId) {
        setParadeJobId(created.jobId);
        setParadeIdeaIdx(0);
      }
      load(true);
    } catch (e) {
      setError(e.message || 'Failed to enqueue');
    } finally {
      setSubmitting(false);
    }
  }, [prompt, activeTeamId, getToken, load]);

  // Cyrillic-free prompts skip Grok and go straight to the image model — but
  // the user still needs to pick a rating, so Generate opens a Safe/Explicit
  // menu instead of submitting. Russian prompts go through Grok as before.
  const [runMenuAnchor, setRunMenuAnchor] = useState(null);
  const generateBtnRef = useRef(null);
  const handleGenerate = useCallback((e) => {
    const text = prompt.trim();
    if (!text || !activeTeamId || submitting) return;
    const hasCyrillic = /[Ѐ-ӿ]/.test(text);
    if (hasCyrillic) {
      submitGenerate(null);
    } else {
      // Ctrl+Enter fires without a MouseEvent, so fall back to the button ref
      // to keep the menu anchored to the button.
      setRunMenuAnchor(e?.currentTarget || generateBtnRef.current);
    }
  }, [prompt, activeTeamId, submitting, submitGenerate]);

  const runDirect = useCallback((rating) => {
    setRunMenuAnchor(null);
    submitGenerate(rating);
  }, [submitGenerate]);

  // Editing: clicking a preset sub-tab swaps the default prompt in only if the
  // user hasn't started typing their own — otherwise we'd clobber their work.
  const switchEditingPreset = useCallback((next) => {
    setEditingPreset(next);
    setEditingPrompt((cur) => {
      const defaults = Object.values(EDITING_DEFAULT_PROMPTS);
      if (cur === '' || defaults.includes(cur.trim())) return EDITING_DEFAULT_PROMPTS[next] || '';
      return cur;
    });
  }, []);

  const uploadSourceFile = useCallback(async (file) => {
    if (!file || !activeTeamId) return;
    setUploadingSource(true);
    setEditingError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('teamId', activeTeamId);
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/image-generation/upload-source`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setEditingImageUrl(data.url || '');
    } catch (e) {
      setEditingError(e.message || 'Upload failed');
    } finally {
      setUploadingSource(false);
    }
  }, [activeTeamId, getToken]);

  const submitEdit = useCallback(async () => {
    if (!editingImageUrl || !activeTeamId || submittingEdit) return;
    setSubmittingEdit(true);
    setEditingError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/image-generation/edit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamId: activeTeamId,
          preset: editingPreset,
          imageUrl: editingImageUrl,
          prompt: editingPrompt
        })
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      // Leave prompt/url in place — user often wants to re-submit with tweaks.
      load(true);
    } catch (e) {
      setEditingError(e.message || 'Failed to enqueue edit');
    } finally {
      setSubmittingEdit(false);
    }
  }, [editingImageUrl, activeTeamId, submittingEdit, getToken, editingPreset, editingPrompt, load]);

  // Opened from the Image Viewer: jumps to the Editing tab with the source
  // image + preset already primed.
  const handleEditFromViewer = useCallback((preset, imageUrl) => {
    setEditingPreset(preset);
    setEditingImageUrl(imageUrl);
    setEditingPrompt(EDITING_DEFAULT_PROMPTS[preset] || '');
    setEditingError('');
    setMainTab('editing');
    setLightboxKey(null);
  }, []);

  const handleDeleteJob = useCallback((jobId) => {
    // Optimistic: tombstone + remove from state right away so the tile
    // disappears before the round-trip. mergeJobs honours the tombstone on
    // subsequent polls in case the server hasn't processed the DELETE yet.
    deletedJobIdsRef.current.add(jobId);
    setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
    setTotal((t) => Math.max(0, t - 1));
    (async () => {
      try {
        const token = await getToken();
        await fetch(`${API_BASE_URL}/api/image-generation/${encodeURIComponent(jobId)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (_) { /* tombstone keeps it hidden either way */ }
    })();
  }, [getToken]);

  const handleDeleteImage = useCallback((jobId, image) => {
    if (!image?.r2Key) return;
    deletedR2KeysRef.current.add(image.r2Key);
    setJobs((prev) => prev.map((j) => (
      j.jobId === jobId
        ? { ...j, images: (j.images || []).filter((im) => im.r2Key !== image.r2Key) }
        : j
    )));
    (async () => {
      try {
        const token = await getToken();
        await fetch(
          `${API_BASE_URL}/api/image-generation/${encodeURIComponent(jobId)}/images?r2Key=${encodeURIComponent(image.r2Key)}`,
          { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (_) { /* tombstone keeps it hidden either way */ }
    })();
  }, [getToken]);

  const handleUpscale = useCallback(async (jobId, r2Key) => {
    const token = await getToken();
    const res = await fetch(
      `${API_BASE_URL}/api/image-generation/${encodeURIComponent(jobId)}/images/upscale`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ r2Key })
      }
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || `HTTP ${res.status}`);
    }
    const updatedJob = await res.json();
    // Splice the one job that was touched so the lightbox sees the new
    // cache-busted URL and `upscaled: true` without a full reload.
    setJobs((prev) => prev.map((j) => (j.jobId === updatedJob.jobId ? updatedJob : j)));
  }, [getToken]);

  const openContext = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(
        `${API_BASE_URL}/api/image-generation/context?teamId=${encodeURIComponent(activeTeamId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.ok ? await res.json() : { content: '' };
      setContextInitial(data.content || '');
    } catch (_) {
      setContextInitial('');
    }
    setContextOpen(true);
  }, [activeTeamId, getToken]);

  const saveContext = useCallback(async (content) => {
    const token = await getToken();
    const res = await fetch(`${API_BASE_URL}/api/image-generation/context`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ teamId: activeTeamId, content })
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || `HTTP ${res.status}`);
    }
    setContextInitial(content);
    setContextOpen(false);
  }, [activeTeamId, getToken]);

  // Tile list — newest first. Every active job gets its own placeholder tile
  // (so a queue backlog is visible rather than hidden behind a single tile).
  // Active jobs can already have partial images, which render right after the
  // placeholder. `galleryItems` is the flat list of navigable images shared
  // with the lightbox; its indices map 1:1 to image-kind tiles in `tiles`.
  const { tiles, galleryItems } = useMemo(() => {
    const t = [];
    const g = [];
    for (const job of jobs) {
      if (job.status === 'failed') {
        t.push({ kind: 'failed', key: `f-${job.jobId}`, job });
        continue;
      }
      // Every active (queued/planning/running/uploading) job gets its own
      // placeholder so a backlog shows up in the gallery rather than silently
      // hiding behind a single tile at the top.
      if (ACTIVE_STATUSES.has(job.status)) {
        t.push({ kind: 'placeholder', key: `p-${job.jobId}`, job });
      }
      {
        const isEditing = job.kind === 'editing';
        // For editing jobs, the "prompt" and "preset" are carried on job.editing
        // directly — no variations. For generation jobs, the preset key is
        // derived from the job's snapshotted defaults + the variation's rating;
        // old jobs (pre-preset refactor) fall back to zImageTurbo.
        const safePreset     = job.settings?.defaultSafePreset     || 'zImageTurbo';
        const explicitPreset = job.settings?.defaultExplicitPreset || 'zImageTurbo';
        // Within a single batch show newest-generated first (top-left), so a
        // freshly landed image slots in right after the placeholder instead
        // of at the end of the batch.
        const imagesDesc = (job.images || [])
          .map((im, idx) => [im, idx])
          .reverse();
        imagesDesc.forEach(([im, idx]) => {
          if (!im.url) return;
          const variation = (job.variations || [])[im.variationIndex || 0];
          const rating = im.rating || variation?.rating || 'safe';
          g.push({
            jobId: job.jobId,
            r2Key: im.r2Key,
            url: im.url,
            previewUrl: im.previewUrl,
            kind: isEditing ? 'editing' : 'generation',
            prompt: isEditing
              ? (job.editing?.promptUser || '')
              : (variation?.prompt || ''),
            tags: im.tags && im.tags.length ? im.tags : (variation?.tags || []),
            rating,
            presetKey: isEditing
              ? (job.editing?.preset || 'imageEdit')
              : (rating === 'explicit' ? explicitPreset : safePreset),
            sourceImageUrl: isEditing ? (job.editing?.sourceImageUrl || '') : '',
            seed: im.seed ?? null,
            timings: im.timings || null,
            width: im.width || 0,
            height: im.height || 0,
            upscaled: !!im.upscaled
          });
          t.push({ kind: 'image', key: `${job.jobId}-${idx}`, job, image: im });
        });
      }
    }
    return { tiles: t, galleryItems: g };
  }, [jobs]);

  // Resolve the parade job from the live jobs list. Two reasons it can
  // disappear: (a) the user cleared its team / it was deleted, (b) it
  // reached a terminal state — in both cases we drop the parade and let
  // the prompt panel come back.
  const paradeJob = useMemo(
    () => (paradeJobId ? jobs.find((j) => j.jobId === paradeJobId) : null),
    [paradeJobId, jobs]
  );
  const paradeIdeas = useMemo(
    () => ((paradeJob?.variations || []).filter((v) => v && v.title)),
    [paradeJob]
  );
  useEffect(() => {
    if (!paradeJobId || !paradeJob) return;
    if (paradeJob.status === 'failed' || paradeJob.status === 'succeeded') {
      setParadeJobId(null);
      setParadeIdeaIdx(0);
      return;
    }
    // Once we've held the last idea long enough, retire the parade so the
    // prompt input comes back even though the job is still running.
    if (paradeIdeas.length > 0 && paradeIdeaIdx >= paradeIdeas.length) {
      setParadeJobId(null);
      setParadeIdeaIdx(0);
    }
  }, [paradeJobId, paradeJob, paradeIdeas.length, paradeIdeaIdx]);

  const lightboxIndex = useMemo(() => {
    if (!lightboxKey) return null;
    const idx = galleryItems.findIndex((g) => g.r2Key === lightboxKey);
    return idx >= 0 ? idx : null;
  }, [lightboxKey, galleryItems]);

  // Close the viewer if the open image disappears (deleted or job removed).
  useEffect(() => {
    if (!lightboxKey) return;
    if (!galleryItems.some((g) => g.r2Key === lightboxKey)) setLightboxKey(null);
  }, [galleryItems, lightboxKey]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header — Generation / Editing tabs + context controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {[
          { id: 'generation', label: 'Generation', Icon: Sparkles },
          { id: 'editing',    label: 'Editing',    Icon: Wand2 }
        ].map(({ id, label, Icon: TabIcon }) => {
          const active = mainTab === id;
          return (
            <Button
              key={id}
              variant={active ? 'contained' : 'text'}
              onClick={() => setMainTab(id)}
              startIcon={<TabIcon size={15} strokeWidth={2} />}
              sx={{
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                py: 0.4,
                px: 1.5,
                minWidth: 0,
                bgcolor: active ? 'rgba(139,92,246,0.2)' : 'transparent',
                color: active ? '#a78bfa' : 'text.secondary',
                '&:hover': {
                  bgcolor: active ? 'rgba(139,92,246,0.28)' : 'rgba(139,92,246,0.08)',
                  color: '#a78bfa'
                }
              }}
            >
              {label}
            </Button>
          );
        })}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          <PodPill getToken={getToken} teamId={activeTeamId} />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => load(false)} disabled={loading}>
              <RotateCcw size={16} />
            </IconButton>
          </Tooltip>
          {mainTab === 'generation' && (
            <Tooltip title="Context">
              <IconButton size="small" onClick={openContext} sx={{ color: 'text.secondary' }}>
                <FileText size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {mainTab === 'generation' && paradeJobId && (
        <IdeaParade
          ideas={paradeIdeas}
          currentIdx={paradeIdeaIdx}
          onAdvance={advanceParade}
        />
      )}

      {mainTab === 'generation' && !paradeJobId && (
      <>
      {/* Prompt form */}
      <Box sx={{
        display: 'flex', gap: 1, alignItems: 'flex-start',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '10px',
        p: 1.25,
        bgcolor: 'rgba(0,0,0,0.15)'
      }}>
        <TextField
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleGenerate();
            }
          }}
          placeholder="Describe the scene…"
          multiline
          maxRows={6}
          fullWidth
          size="small"
          disabled={submitting}
        />
        {(() => {
          const disabled = submitting || !prompt.trim();
          const showHint = !disabled;
          return (
            <Button
              ref={generateBtnRef}
              variant="contained"
              onClick={handleGenerate}
              disabled={disabled}
              sx={{
                textTransform: 'none',
                bgcolor: '#8b5cf6',
                '&:hover': { bgcolor: '#7c3aed' },
                flexShrink: 0,
                minWidth: 120,
                pt: showHint ? 0.9 : 0.75,
                pb: showHint ? 0.35 : 0.75,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1.15
              }}
            >
              {submitting
                ? <CircularProgress size={16} color="inherit" />
                : (
                  <>
                    <Box component="span" sx={{ fontWeight: 600 }}>Generate</Box>
                    {showHint && (
                      <Box component="span" sx={{ fontSize: '0.62rem', opacity: 0.45, fontWeight: 500 }}>
                        Ctrl&nbsp;+&nbsp;Enter
                      </Box>
                    )}
                  </>
                )}
            </Button>
          );
        })()}
        <Menu
          anchorEl={runMenuAnchor}
          open={!!runMenuAnchor}
          onClose={() => setRunMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => runDirect('safe')}>
            <ListItemIcon sx={{ minWidth: 28, color: '#94a3b8' }}>
              <ShieldCheck size={16} />
            </ListItemIcon>
            <ListItemText
              primary="Run Safe"
              secondary="Skip Grok — use prompt as-is, rated safe"
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.7rem' }}
            />
          </MenuItem>
          <MenuItem onClick={() => runDirect('explicit')}>
            <ListItemIcon sx={{ minWidth: 28, color: '#f87171' }}>
              <Flame size={16} />
            </ListItemIcon>
            <ListItemText
              primary="Run Explicit"
              secondary="Skip Grok — use prompt as-is, rated explicit"
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.7rem' }}
            />
          </MenuItem>
        </Menu>
      </Box>

      {error && <Alert severity="error" sx={{ fontSize: '0.8rem' }}>{error}</Alert>}
      </>
      )}

      {mainTab === 'editing' && (
      <>
        {/* Editing sub-tabs */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {EDITING_PRESETS_FULL.map(({ key, label, Icon: PIcon }) => {
            const active = editingPreset === key;
            return (
              <Button
                key={key}
                size="small"
                variant={active ? 'contained' : 'outlined'}
                startIcon={<PIcon size={14} />}
                onClick={() => switchEditingPreset(key)}
                sx={{
                  textTransform: 'none',
                  bgcolor: active ? 'rgba(139,92,246,0.2)' : 'transparent',
                  color: active ? '#a78bfa' : 'text.secondary',
                  borderColor: active ? 'rgba(139,92,246,0.5)' : 'rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: active ? 'rgba(139,92,246,0.28)' : 'rgba(139,92,246,0.08)', color: '#a78bfa' }
                }}
              >
                {label}
              </Button>
            );
          })}
        </Box>

        {/* Dropzone + URL */}
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer?.files?.[0];
            if (file) uploadSourceFile(file);
          }}
          onClick={() => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.onchange = () => { const f = input.files?.[0]; if (f) uploadSourceFile(f); };
            input.click();
          }}
          sx={{
            border: '2px dashed',
            borderColor: dragOver ? 'rgba(139,92,246,0.55)' : 'rgba(255,255,255,0.12)',
            bgcolor: dragOver ? 'rgba(139,92,246,0.08)' : 'rgba(0,0,0,0.15)',
            borderRadius: '10px',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            cursor: 'pointer',
            transition: 'all 0.12s ease',
            '&:hover': { borderColor: 'rgba(139,92,246,0.4)' }
          }}
        >
          {editingImageUrl ? (
            <Box
              component="img"
              src={editingImageUrl}
              alt=""
              sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: '6px', flexShrink: 0, border: '1px solid rgba(255,255,255,0.1)' }}
            />
          ) : (
            <Box sx={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled', flexShrink: 0 }}>
              <Upload size={28} />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: editingImageUrl ? 'text.primary' : 'text.secondary' }}>
              {uploadingSource ? 'Uploading…' : editingImageUrl ? 'Source image loaded' : 'Drop an image here or click to pick'}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', wordBreak: 'break-all' }}>
              {editingImageUrl || 'Converted to webp and stored on R2 before editing.'}
            </Typography>
          </Box>
          {editingImageUrl && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); setEditingImageUrl(''); }}
              sx={{ color: 'text.secondary', flexShrink: 0 }}
            >
              <X size={16} />
            </IconButton>
          )}
        </Box>

        {/* Prompt + submit */}
        <Box sx={{
          display: 'flex', gap: 1, alignItems: 'flex-start',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '10px',
          p: 1.25,
          bgcolor: 'rgba(0,0,0,0.15)'
        }}>
          <TextField
            value={editingPrompt}
            onChange={(e) => setEditingPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submitEdit(); } }}
            placeholder="Edit instruction (optional for upscale/relight)…"
            multiline
            maxRows={6}
            fullWidth
            size="small"
            disabled={submittingEdit}
          />
          <Button
            variant="contained"
            onClick={submitEdit}
            disabled={submittingEdit || uploadingSource || !editingImageUrl}
            sx={{
              textTransform: 'none',
              bgcolor: '#8b5cf6',
              '&:hover': { bgcolor: '#7c3aed' },
              flexShrink: 0,
              minWidth: 120,
              py: 0.75,
              lineHeight: 1.15
            }}
          >
            {submittingEdit ? <CircularProgress size={16} color="inherit" /> : 'Run Edit'}
          </Button>
        </Box>

        {editingError && <Alert severity="error" sx={{ fontSize: '0.8rem' }} onClose={() => setEditingError('')}>{editingError}</Alert>}
      </>
      )}

      {/* Gallery */}
      {loading && jobs.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : tiles.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center', color: 'text.disabled' }}>
          <Typography variant="body2">
            Nothing generated yet. Type a prompt above and hit Generate.
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 1.25
        }}>
          {tiles.map((t) => {
            if (t.kind === 'placeholder') return <PlaceholderTile key={t.key} job={t.job} />;
            if (t.kind === 'failed') return <FailedTile key={t.key} job={t.job} onDeleteJob={handleDeleteJob} />;
            return (
              <ImageTile
                key={t.key}
                image={t.image}
                job={t.job}
                onDeleteImage={handleDeleteImage}
                onOpen={setLightboxKey}
              />
            );
          })}
        </Box>
      )}

      {!loading && tiles.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.disabled">
            {tiles.length} tiles from {hasMore ? `${jobs.length} of ${total}` : total} generations
          </Typography>
          {hasMore && (
            <Button
              size="small"
              variant="outlined"
              onClick={loadMore}
              disabled={loadingMore}
              sx={{ ml: 1 }}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          )}
        </Box>
      )}

      <ContextDialog
        open={contextOpen}
        initial={contextInitial}
        onClose={() => setContextOpen(false)}
        onSave={saveContext}
      />
      <LightboxDialog
        items={galleryItems}
        index={lightboxIndex}
        onIndexChange={(i) => setLightboxKey(galleryItems[i]?.r2Key || null)}
        onClose={() => setLightboxKey(null)}
        onUpscale={handleUpscale}
        onEdit={handleEditFromViewer}
        getToken={getToken}
        teamId={activeTeamId}
      />
    </Box>
  );
}
